// src/stats/LineChart.tsx

interface LineChartProps {
  points: { date: string; value: number }[]
  metric: string
  width?: number
  height?: number
  color?: string
}

/**
 * Graphe courbe SVG avec axe Y et repères de dates.
 * Utilisé pour les séries temporelles dans StatsPage.
 */
export function LineChart({
  points,
  width = 600,
  height = 200,
  color = 'var(--arancio)',
}: LineChartProps) {
  if (points.length === 0) {
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--dim)',
          fontSize: '0.8125rem',
          border: '1px solid var(--line)',
          borderRadius: '8px',
          background: 'var(--surface)',
        }}
      >
        Aucune donnée sur la période
      </div>
    )
  }

  const padLeft = 48
  const padRight = 12
  const padTop = 12
  const padBottom = 28

  const w = width - padLeft - padRight
  const h = height - padTop - padBottom

  const values = points.map((p) => p.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min === 0 ? 1 : max - min

  const toX = (i: number) => padLeft + (i / Math.max(points.length - 1, 1)) * w
  const toY = (v: number) => padTop + h - ((v - min) / range) * h

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(p.value).toFixed(1)}`)
    .join(' ')

  const fillPath =
    linePath +
    ` L${toX(points.length - 1).toFixed(1)},${(padTop + h).toFixed(1)}` +
    ` L${toX(0).toFixed(1)},${(padTop + h).toFixed(1)} Z`

  // Graduations Y (4 niveaux)
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    value: min + t * range,
    y: padTop + h * (1 - t),
  }))

  // Repères de date (max 5 labels)
  const dateStep = Math.max(1, Math.floor(points.length / 5))
  const dateLabels = points
    .filter((_, i) => i % dateStep === 0 || i === points.length - 1)
    .map((p, _, arr) => ({
      date: p.date.slice(5), // MM-DD
      x: toX(points.indexOf(p)),
      isFirst: p === arr[0],
    }))

  const gradId = `chart-grad-${Math.random().toString(36).slice(2, 7)}`

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-label="Graphe de série temporelle"
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.2} />
          <stop offset="100%" stopColor={color} stopOpacity={0.02} />
        </linearGradient>
      </defs>

      {/* Lignes de grille Y */}
      {yTicks.map((tick) => (
        <g key={tick.value}>
          <line
            x1={padLeft}
            y1={tick.y.toFixed(1)}
            x2={padLeft + w}
            y2={tick.y.toFixed(1)}
            stroke="var(--line)"
            strokeWidth="1"
          />
          <text
            x={padLeft - 6}
            y={(tick.y + 4).toFixed(1)}
            textAnchor="end"
            fill="var(--dim)"
            fontSize="10"
            fontFamily="var(--mono)"
          >
            {tick.value >= 1000
              ? `${(tick.value / 1000).toFixed(1)}k`
              : tick.value.toFixed(tick.value < 1 ? 2 : 0)}
          </text>
        </g>
      ))}

      {/* Zone remplie */}
      <path d={fillPath} fill={`url(#${gradId})`} stroke="none" />

      {/* Courbe */}
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Labels de dates */}
      {dateLabels.map((dl) => (
        <text
          key={dl.date + dl.x}
          x={dl.x.toFixed(1)}
          y={height - 6}
          textAnchor="middle"
          fill="var(--dim)"
          fontSize="10"
          fontFamily="var(--mono)"
        >
          {dl.date}
        </text>
      ))}
    </svg>
  )
}
